<script setup>
import { computed, ref } from 'vue'

import WithLabel from '@/components/Form/WithLabel.vue'

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  name: {
    type: String,
    required: true,
  },
  hasError: Boolean,
  errorMessage: {
    type: String,
    default: '',
  },
})

const FIELDS = {
  TEXT: 'text',
  PASSWORD: 'password',
}

defineOptions({
  inheritAttrs: false,
})

const model = defineModel({
  type: [String, Number],
  required: true,
})

const isPasswordVisible = ref(false)

const togglePasswordVisibility = () => {
  isPasswordVisible.value = !isPasswordVisible.value
}

const isPasswordField = computed(() => props.type === FIELDS.PASSWORD)

const currentInputType = computed(() => {
  if (isPasswordField.value) {
    return isPasswordVisible.value ? FIELDS.TEXT : FIELDS.PASSWORD
  }
  return props.type
})
</script>

<template>
  <WithLabel
    :label="label"
    :name="name"
    :has-error="hasError"
    :error-message="errorMessage"
  >
    <template #rightOfLabel>
      <slot />
    </template>
    <input
      v-bind="$attrs"
      v-model="model"
      :name="name"
      :type="currentInputType"
      class="block w-full border-none rounded-md shadow-sm bg-n-alpha-black2 appearance-none outline outline-1 focus:outline focus:outline-1 text-n-slate-12 placeholder:text-n-slate-10 sm:text-sm sm:leading-6 px-3 py-3"
      :class="{
        'error outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8':
          hasError,
        'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
          !hasError,
        'pr-10': isPasswordField,
      }"
    />
    <button
      v-if="isPasswordField"
      type="button"
      class="absolute inset-y-0 right-0 flex items-center pr-3 text-n-slate-11 hover:text-n-slate-12"
      :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
      :aria-pressed="isPasswordVisible"
      @click="togglePasswordVisibility"
    >
      <span
        class="size-4"
        :class="isPasswordVisible ? 'i-lucide-eye-off' : 'i-lucide-eye'"
      />
    </button>
  </WithLabel>
</template>
